<template>
  <div>
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transistion"
    >
      <template v-slot:activator="{ on }">
        <v-btn class="white--text" :color="color" v-on="on">
          <v-icon v-if="!!icon">{{ icon }}</v-icon>
          <span v-else>{{ text }}</span>
        </v-btn>
      </template>

      <v-card>
        <v-toolbar dark color="mdred">
          <v-btn icon dark @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t("user.selection.title") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click="confirm">{{
              $t("app.buttons.confirm")
            }}</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-container fill-height fluid>
          <v-layout align-center justify-center>
            <v-flex sm12 md6>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      $t("app.forms.filters")
                    }}</v-list-item-title>
                    <v-list-item-subtitle>{{
                      $t("user.selection.choose")
                    }}</v-list-item-subtitle>
                    <v-text-field
                      v-model="search"
                      prepend-icon="person"
                      :label="$t('app.forms.search')"
                      @input="pagination.page = 1"
                    ></v-text-field>
                    <v-spacer></v-spacer>
                    <v-switch
                      v-model="filters.teachers"
                      :label="$t('user.selection.filters.teachers')"
                      style="margin-right:16px;"
                    ></v-switch>
                    <v-switch
                      v-model="filters.admins"
                      :disabled="!filters.teachers"
                      :label="$t('user.selection.filters.admins')"
                    ></v-switch>
                    <v-spacer></v-spacer>
                    <v-switch
                      v-model="filters.students"
                      :label="$t('user.selection.filters.students')"
                    ></v-switch>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-divider></v-divider>

              <Progression v-if="!users"></Progression>
              <v-list v-else three-line>
                <v-list-item-group
                  v-if="!refreshingList"
                  v-model="selection"
                  multiple
                >
                  <template v-for="user in filtered">
                    <v-list-item :key="user.id" :value="user">
                      <template v-slot:default="{ active, toggle }">
                        <v-list-item-icon>
                          <v-avatar size="50" class="grey lighten-2">
                            <img
                              src="https://previews.123rf.com/images/yupiramos/yupiramos1705/yupiramos170527873/78659562-musician-playing-guitar-avatar-vector-illustration-design.jpg"
                            />
                          </v-avatar>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="formatUser(user)"
                          ></v-list-item-title>
                          <v-list-item-subtitle
                            v-text="user.email"
                          ></v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-checkbox
                            :input-value="active"
                            :true-value="user"
                            @click="toggle"
                          ></v-checkbox>
                        </v-list-item-action>
                      </template>
                    </v-list-item>
                  </template>
                </v-list-item-group>
              </v-list>

              <div class="subtitle-2">
                {{ $t("user.selection.selected-prefix") }}
                {{ $tc("user.selection.selected-count", selection.length) }}
              </div>

              <v-divider></v-divider>

              <v-card-actions>
                <v-btn dark color="mdred" @click="clear">
                  {{ $t("app.buttons.clear") }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn icon :disabled="pagination.page == 1" @click="prev()">
                  <v-icon>navigate_before</v-icon>
                </v-btn>
                {{ pagination.page }} of
                {{ limit }}
                <v-btn
                  icon
                  :disabled="pagination.page == limit"
                  @click="next()"
                >
                  <v-icon>navigate_next</v-icon>
                </v-btn>
              </v-card-actions>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Progression from "@/components/Progression";

export default {
  components: {
    Progression
  },

  props: {
    icon: {
      type: String,
      default: null
    },
    text: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: "primary"
    },
    except: {
      type: Array,
      default: null
    }
  },

  data: () => ({
    dialog: false,
    users: null,
    selection: [],
    refreshingList: false,
    pagination: {
      size: 4,
      page: 1
    },
    filters: {
      students: true,
      teachers: true,
      admins: true
    },
    search: ""
  }),

  computed: {
    /**
     * The pool of users available after a search operation.
     */
    usersPool: function() {
      if (!this.users) {
        return [];
      }

      let self = this;
      // Get the search result
      const result = this.users.filter(function(user) {
        // Reject users with an email contained in the 'except' prop
        if (self.except) {
          var reject = false;
          self.except.forEach(function(email) {
            if (email === user.email) {
              reject = true;
            }
          });
          if (reject) {
            return false;
          }
        }
        // Apply the search filter (or not)
        if (
          self.search &&
          // On the full name
          !(user.firstname + " " + user.lastname)
            .toLowerCase()
            .includes(self.search.toLowerCase()) &&
          // On the email
          !user.email.toLowerCase().includes(self.search.toLowerCase())
        ) {
          return false;
        }
        return true;
      });
      return result;
    },

    /**
     * The remaining users that pass the filters.
     */
    filtered: function() {
      let self = this;
      // Get the filtered result
      var result = this.usersPool.filter(function(user) {
        // Apply filters
        switch (user.role) {
          case "student":
            return self.filters.students;
          case "teacher":
            return self.filters.teachers;
          case "admin":
            return self.filters.teachers && self.filters.admins;
        }
        return true;
      });
      // Last element (exclusive) of the pagination
      const end = this.pagination.size * this.pagination.page;
      // Apply and return the pagination
      return result.slice(end - this.pagination.size, end);
    },

    /**
     * Pagination' limit.
     */
    limit: function() {
      const limit = Math.ceil(this.usersPool.length / this.pagination.size);
      // Still use 1 instead of 0
      return limit != 0 ? limit : 1;
    }
  },

  watch: {
    filters: {
      handler: function() {
        this.blinkList();
      },
      deep: true
    },
    search: {
      handler: function() {
        this.blinkList();
      }
    },
    pagination: {
      handler: function() {
        this.blinkList();
      },
      deep: true
    }
  },

  mounted: function() {
    this.getUsers();
  },

  methods: {
    next() {
      if (this.pagination.page < this.limit) {
        this.pagination.page++;
      }
    },

    prev() {
      if (this.pagination.page > 1) {
        this.pagination.page--;
      }
    },

    formatUser(user) {
      const name = user.firstname + " " + user.lastname;
      const role = " (" + this.roleToText(user.role) + ")";
      return name + role;
    },

    roleToText(role) {
      if (!role) {
        return this.$t("user.roles.undefined");
      }
      return this.$t("user.roles." + role + ".title");
    },

    /**
     * This hack makes sure the elements are selected after changing some filters.
     * The list "blinks" fast enough, the user won't see it happen.
     * While the list isn't rendered, it simply can't alter the selection.
     * So use this method only to perform actions that causes unwanted selection alterations.
     */
    blinkList() {
      this.refreshingList = true;
      this.$nextTick(async function() {
        this.refreshingList = false;
      });
    },

    getUsers() {
      this.$http
        .get("/users", {
          withCredentials: true,
          credentials: "include",
          mode: "cors"
        })
        .then(request => this.fetchSuccessful(request.data))
        .catch(error => this.fetchFailed(error));
    },

    fetchSuccessful(req) {
      this.users = req.users;
      this.$emit("fetched");
    },

    fetchFailed(error) {
      if (process.env.NODE_ENV !== "production") {
        console.log(error);
      }
      this.$store.dispatch(
        "system/setStatus",
        {
          type: "error",
          message: "Users fetching failed."
        },
        { root: true }
      );
    },

    confirm() {
      this.$emit("confirmed", this.selection);
      this.dialog = false;
    },

    clear() {
      this.selection = [];
    }
  }
};
</script>
